import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react'
import socketService, { SOCKET_EVENTS } from '../services/socket'
import { useAuth } from './AuthContext'
import api from '../services/api'
import { instalarResyncAoVoltar } from '../utils/resync'

interface SocketContextData {
  isConnected: boolean
  isReconnecting: boolean
  reconnectAttempts: number
  isWedropBackendConnected: boolean
  joinChat: (chatId: number) => void
  leaveChat: (chatId: number) => void
  startTyping: (chatId: number) => void
  stopTyping: (chatId: number) => void
  markMessagesRead: (chatId: number) => void
  onNewMessage: (callback: (data: any) => void) => () => void
  onChatNew: (callback: (data: any) => void) => () => void
  onChatStarted: (callback: (data: any) => void) => () => void
  onChatFinished: (callback: (data: any) => void) => () => void
  onChatTransferred: (callback: (data: any) => void) => () => void
  onChatStatusChanged: (callback: (data: any) => void) => () => void
  onChatUpdated: (callback: (data: any) => void) => () => void
  onUnreadUpdate: (callback: (data: any) => void) => () => void
  onTypingStarted: (callback: (data: any) => void) => () => void
  onTypingStopped: (callback: (data: any) => void) => () => void
  onMessagesRead: (callback: (data: any) => void) => () => void
  onMessageEdited: (callback: (data: any) => void) => () => void
  onMessageDeleted: (callback: (data: any) => void) => () => void
  onMessageStatus: (callback: (data: any) => void) => () => void
}

const SocketContext = createContext<SocketContextData>({} as SocketContextData)

export function useSocket() {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }
  return context
}

// Modal de reconexão
function ReconnectingModal({ 
  isReconnecting, 
  reconnectAttempts,
  isWedropBackendConnected,
  isSocketConnected,
}: { 
  isReconnecting: boolean
  reconnectAttempts: number
  isWedropBackendConnected: boolean
  isSocketConnected: boolean
}) {
  // Mostrar modal se estiver reconectando OU se alguma conexão estiver fora
  const shouldShow = isReconnecting || (!isSocketConnected && reconnectAttempts > 0)
  
  if (!shouldShow) return null

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        style={{
          backgroundColor: '#1a1a2e',
          borderRadius: '16px',
          padding: '40px',
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%',
          border: '1px solid #2d2d44',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        {/* Animação de loading */}
        <div
          style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 24px',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              border: '4px solid transparent',
              borderTopColor: '#ECC94B',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '60px',
              height: '60px',
              top: '10px',
              left: '10px',
              border: '4px solid transparent',
              borderTopColor: '#4299E1',
              borderRadius: '50%',
              animation: 'spin 1.5s linear infinite reverse',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: '40px',
              height: '40px',
              top: '20px',
              left: '20px',
              border: '4px solid transparent',
              borderTopColor: '#48BB78',
              borderRadius: '50%',
              animation: 'spin 2s linear infinite',
            }}
          />
        </div>

        <h2
          style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 600,
            marginBottom: '12px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          Reconectando ao servidor...
        </h2>

        <p
          style={{
            color: '#a0aec0',
            fontSize: '14px',
            marginBottom: '20px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            lineHeight: 1.5,
          }}
        >
          Aguarde enquanto restabelecemos a conexão.
          <br />
          Não feche esta janela.
        </p>

        {/* Status das conexões */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: '#2d2d44',
              borderRadius: '8px',
            }}
          >
            <span style={{ color: '#cbd5e0', fontSize: '13px' }}>Socket Principal</span>
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: isSocketConnected ? '#48BB78' : '#FC8181',
              }}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              backgroundColor: '#2d2d44',
              borderRadius: '8px',
            }}
          >
            <span style={{ color: '#cbd5e0', fontSize: '13px' }}>Backend WeDrop</span>
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: isWedropBackendConnected ? '#48BB78' : '#FC8181',
              }}
            />
          </div>
        </div>

        {/* Contador de tentativas */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#2d2d44',
            borderRadius: '8px',
          }}
        >
          <div
            style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#ECC94B',
              borderRadius: '50%',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
          <span
            style={{
              color: '#cbd5e0',
              fontSize: '13px',
              fontFamily: 'system-ui, -apple-system, sans-serif',
            }}
          >
            Tentativa {reconnectAttempts}
          </span>
        </div>

        {/* Estilos de animação */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; transform: scale(1); }
              50% { opacity: 0.5; transform: scale(1.2); }
            }
          `}
        </style>
      </div>
    </div>
  )
}

export function SocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [isConnected, setIsConnected] = useState(false)
  const [isReconnecting, setIsReconnecting] = useState(false)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const [isWedropBackendConnected, setIsWedropBackendConnected] = useState(true)
  const wasConnectedRef = useRef(false)
  const connectionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Verificar status de conexão com o backend WeDrop periodicamente
  const checkConnectionStatus = useCallback(async () => {
    try {
      const response = await api.get('/connection-status')
      const { connections } = response.data
      setIsWedropBackendConnected(connections?.wedropBackend ?? true)
    } catch {
      // Se não conseguir verificar, assume que está ok para não bloquear
      setIsWedropBackendConnected(true)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      // Voltar do segundo plano (celular) reconecta e manda as telas buscarem de novo.
      instalarResyncAoVoltar()
      const token = localStorage.getItem('wedrop_support_token')
      if (token) {
        const socket = socketService.connect(token)

        const handleConnect = () => {
          console.log('[SocketContext] Socket connected, joining agents room')
          setIsConnected(true)
          setIsReconnecting(false)
          setReconnectAttempts(0)
          wasConnectedRef.current = true
          socketService.joinAgents()
          
          // Verificar status de conexão com WeDrop backend
          checkConnectionStatus()
        }

        const handleDisconnect = (reason: string) => {
          console.log('[SocketContext] Socket disconnected:', reason)
          setIsConnected(false)
          // Só mostra modal de reconexão se já estava conectado antes
          if (wasConnectedRef.current) {
            setIsReconnecting(true)
          }
        }

        const handleReconnectAttempt = (attemptNumber: number) => {
          console.log('[SocketContext] Reconnect attempt:', attemptNumber)
          setReconnectAttempts(attemptNumber)
          setIsReconnecting(true)
        }

        const handleReconnect = (attemptNumber: number) => {
          console.log(`[SocketContext] Reconnected after ${attemptNumber} attempts`)
          setIsConnected(true)
          setIsReconnecting(false)
          setReconnectAttempts(0)
          
          // Verificar status de conexão com WeDrop backend após reconectar
          checkConnectionStatus()
        }

        const handleConnectError = (error: Error) => {
          console.error('[SocketContext] Connection error:', error.message)
          if (wasConnectedRef.current) {
            setIsReconnecting(true)
            setReconnectAttempts(prev => prev + 1)
          }
        }

        socket.on('connect', handleConnect)
        socket.on('disconnect', handleDisconnect)
        socket.io.on('reconnect_attempt', handleReconnectAttempt)
        socket.io.on('reconnect', handleReconnect)
        socket.on('connect_error', handleConnectError)

        // If already connected, join agents immediately
        if (socket.connected) {
          handleConnect()
        }

        return () => {
          socket.off('connect', handleConnect)
          socket.off('disconnect', handleDisconnect)
          socket.io.off('reconnect_attempt', handleReconnectAttempt)
          socket.io.off('reconnect', handleReconnect)
          socket.off('connect_error', handleConnectError)
          
          if (connectionCheckIntervalRef.current) {
            clearInterval(connectionCheckIntervalRef.current)
          }
        }
      }
    } else {
      socketService.disconnect()
      setIsConnected(false)
      setIsReconnecting(false)
      setReconnectAttempts(0)
      wasConnectedRef.current = false
      
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current)
      }
    }

    return () => {
      socketService.disconnect()
      if (connectionCheckIntervalRef.current) {
        clearInterval(connectionCheckIntervalRef.current)
      }
    }
  }, [isAuthenticated, checkConnectionStatus])

  const createEventSubscriber = useCallback((event: string) => {
    return (callback: (data: any) => void) => {
      // Remove any existing callback with same reference first to avoid duplicates
      socketService.off(event, callback)
      socketService.on(event, callback)
      return () => {
        socketService.off(event, callback)
      }
    }
  }, [])

  // Memoizar funções para evitar re-renders desnecessários
  const joinChat = useCallback((chatId: number) => socketService.joinChat(chatId), [])
  const leaveChat = useCallback((chatId: number) => socketService.leaveChat(chatId), [])
  const startTyping = useCallback((chatId: number) => socketService.startTyping(chatId), [])
  const stopTyping = useCallback((chatId: number) => socketService.stopTyping(chatId), [])
  const markMessagesRead = useCallback((chatId: number) => socketService.markMessagesRead(chatId), [])

  // Memoizar event subscribers para evitar recriação e listeners duplicados
  const onNewMessage = useCallback(createEventSubscriber(SOCKET_EVENTS.MESSAGE.NEW), [createEventSubscriber])
  const onChatNew = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.NEW), [createEventSubscriber])
  const onChatStarted = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.STARTED), [createEventSubscriber])
  const onChatFinished = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.FINISHED), [createEventSubscriber])
  const onChatTransferred = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.TRANSFERRED), [createEventSubscriber])
  const onChatStatusChanged = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.STATUS_CHANGED), [createEventSubscriber])
  const onChatUpdated = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.UPDATED), [createEventSubscriber])
  const onUnreadUpdate = useCallback(createEventSubscriber(SOCKET_EVENTS.CHAT.UNREAD_UPDATE), [createEventSubscriber])
  const onTypingStarted = useCallback(createEventSubscriber(SOCKET_EVENTS.TYPING.STARTED), [createEventSubscriber])
  const onTypingStopped = useCallback(createEventSubscriber(SOCKET_EVENTS.TYPING.STOPPED), [createEventSubscriber])
  const onMessagesRead = useCallback(createEventSubscriber(SOCKET_EVENTS.MESSAGE.READ), [createEventSubscriber])
  const onMessageEdited = useCallback(createEventSubscriber(SOCKET_EVENTS.MESSAGE.EDITED), [createEventSubscriber])
  const onMessageDeleted = useCallback(createEventSubscriber(SOCKET_EVENTS.MESSAGE.DELETED), [createEventSubscriber])
  const onMessageStatus = useCallback(createEventSubscriber(SOCKET_EVENTS.MESSAGE.STATUS), [createEventSubscriber])

  const value: SocketContextData = {
    isConnected,
    isReconnecting,
    reconnectAttempts,
    isWedropBackendConnected,
    joinChat,
    leaveChat,
    startTyping,
    stopTyping,
    markMessagesRead,
    onNewMessage,
    onChatNew,
    onChatStarted,
    onChatFinished,
    onChatTransferred,
    onChatStatusChanged,
    onChatUpdated,
    onUnreadUpdate,
    onTypingStarted,
    onTypingStopped,
    onMessagesRead,
    onMessageEdited,
    onMessageDeleted,
    onMessageStatus,
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
      <ReconnectingModal 
        isReconnecting={isReconnecting} 
        reconnectAttempts={reconnectAttempts}
        isWedropBackendConnected={isWedropBackendConnected}
        isSocketConnected={isConnected}
      />
    </SocketContext.Provider>
  )
}
